import { LoadingStatusType, PayrollDocumentSettings, TypeComponentNoti } from "@/enums/enums";
import { GetErrorMessageByPayrollExceptionCode } from "@/helper/exceptionCodeHelper";
import { ConvertToSettingModel, ValidateSettingTemplate } from "@/helper/settingHelper";
import { i18n } from "@/i18n";
import { MerchantAddress } from "@/model/merchant/merchantAddress";
import { OnBoardingModel } from "@/model/onBoarding/onBoarding";
import { SettingDetailModel } from "@/model/setting/settingDetailModel";
import { CurrentDocumentSettings, DocumentSettings, SettingModel } from "@/model/setting/settingModel";
import API from "@/service/Index";
import { ConvertToMerchantAddressList, ConvertToMerchantDetail } from "@/service/merchant/merchantFacade";
import { ConvertToOnBoarding, ConvertToSettingDetail } from "@/service/setting/settingFacade";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";

@Module({ namespaced: true })
export default class Setting extends VuexModule {
  private settingDetail: any = null;
  private settingDetailLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  private settingUpdateLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  private merchantDetailLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  private updatePayrollContactLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  private merchantDetail: any = null;
  private onBoarding: any = {};
  private onBoardingLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  private updateOnBoardingStatus: LoadingStatusType = LoadingStatusType.None;
  private merchantAddressList: Array<MerchantAddress> = [];
  private merchantAddressListLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  private documentSettingsLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  private documentSettings: DocumentSettings | null = null;
  private updateDocumentSettingsLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  private updateDocumentSettings: DocumentSettings | null = null;
  private currentDocumentSettings: CurrentDocumentSettings | null = null;

  get getSettingsDetail(): SettingDetailModel {
    return this.settingDetail;
  }
  get getSettingsDetailLoadingStatus(): LoadingStatusType {
    return this.settingDetailLoadingStatus;
  }
  get getSettingUpdateLoadingStatus(): LoadingStatusType {
    return this.settingUpdateLoadingStatus;
  }
  get getMerchantDetailLoadingStatus(): LoadingStatusType {
    return this.merchantDetailLoadingStatus;
  }
  get getUpdatePayrollContactLoadingStatus(): LoadingStatusType {
    return this.updatePayrollContactLoadingStatus;
  }
  get getMerchantDetail(): any {
    return this.merchantDetail;
  }
  get getOnBoarding(): OnBoardingModel {
    return this.onBoarding;
  }
  get getOnBoardingLoadingStatus(): LoadingStatusType {
    return this.onBoardingLoadingStatus;
  }
  get getUpdateOnBoardingStatus(): LoadingStatusType {
    return this.updateOnBoardingStatus;
  }
  get getMerchantAddressListLoadingStatus(): LoadingStatusType {
    return this.merchantAddressListLoadingStatus;
  }
  get getMerchantAddressList(): Array<MerchantAddress> {
    return this.merchantAddressList;
  }

  get getDocumentSettingsLoadingStatus(): LoadingStatusType {
    return this.documentSettingsLoadingStatus;
  }
  get getDocumentSettings() {
    return this.documentSettings;
  }
  get getUpdateDocumentSettingsLoadingStatus(): LoadingStatusType {
    return this.updateDocumentSettingsLoadingStatus;
  }
  get getUpdateDocumentSettings() {
    return this.updateDocumentSettings;
  }
  get getCurrentDocumentSettings() {
    return this.currentDocumentSettings;
  }

  @Mutation
  public MutationOnBoarding(onBoarding: OnBoardingModel) {
    this.onBoarding = onBoarding;
  }
  @Mutation
  public MutationOnBoardingLoadingStatus(status: LoadingStatusType) {
    this.onBoardingLoadingStatus = status;
  }
  @Mutation
  public MutationUpdateOnBoardingStatus(status: LoadingStatusType) {
    this.updateOnBoardingStatus = status;
  }

  @Mutation
  public MutationSettingDetail(settingDetail: SettingDetailModel) {
    this.settingDetail = settingDetail;
  }
  @Mutation
  public MutationSettingsDetailLoadingStatus(status: LoadingStatusType) {
    this.settingDetailLoadingStatus = status;
  }
  @Mutation
  public MutationSettingsUpdateLoadingStatus(status: LoadingStatusType) {
    this.settingUpdateLoadingStatus = status;
  }

  @Mutation
  public MutationMerchantDetailLoadingStatus(status: LoadingStatusType) {
    this.merchantDetailLoadingStatus = status;
  }
  @Mutation
  public MutationUpdatePayrollContactLoadingStatus(status: LoadingStatusType) {
    this.updatePayrollContactLoadingStatus = status;
  }

  @Mutation
  public MutationMerchantDetail(merchantDetail: any) {
    this.merchantDetail = merchantDetail;
  }

  @Mutation
  public MutationMerchantAddressList(addressList: Array<MerchantAddress>) {
    this.merchantAddressList = addressList;
  }

  @Mutation
  public MutationMerchantAddressListLoadingStatus(status: LoadingStatusType) {
    this.merchantAddressListLoadingStatus = status;
  }

  @Mutation
  public MutationDocumentSettingsLoadingStatus(status: LoadingStatusType) {
    this.documentSettingsLoadingStatus = status;
  }

  @Mutation
  public MutationDocumentSettings(documentSettings: Setting["documentSettings"]) {
    this.documentSettings = documentSettings;
    this.currentDocumentSettings = documentSettings ? { certifiedSetting: documentSettings.certifiedSetting } : null;
  }

  @Mutation
  public MutationUpdateDocumentSettingsLoadingStatus(status: LoadingStatusType) {
    this.updateDocumentSettingsLoadingStatus = status;
  }

  @Mutation
  public MutationUpdateDocumentSettings(updateDocumentSettings: NonNullable<Setting["updateDocumentSettings"]>) {
    this.updateDocumentSettings = updateDocumentSettings;
  }

  @Mutation MutationCurrentDocumentSettings(currentDocumentSettings: CurrentDocumentSettings | null) {
    this.currentDocumentSettings = currentDocumentSettings;
  }

  @Action
  public DoGetSettingDetail() {
    if (this.settingDetailLoadingStatus !== LoadingStatusType.Loading) {
      this.context.dispatch("Loading/IsLoading", true, { root: true });
      this.context.commit("MutationSettingsDetailLoadingStatus", LoadingStatusType.Loading);
      API.SettingService.DoGetSettingDetail().then((resSettingDetail) => {
        if (resSettingDetail.status.IsSuccess()) {
          const detail = ConvertToSettingDetail(resSettingDetail.data.detail);
          this.context.commit("MutationSettingDetail", detail);
        }
        this.context.commit("MutationSettingsDetailLoadingStatus", LoadingStatusType.Success);
        this.context.dispatch("Loading/IsLoading", false, { root: true });
      });
    }
  }

  @Action
  public DoUpdateSetting(input: any) {
    let alertMessage: any = "";
    let typeMessage: TypeComponentNoti = TypeComponentNoti.success;
    if (this.settingUpdateLoadingStatus !== LoadingStatusType.Loading) {
      const validateSettingTemplate = ValidateSettingTemplate(input.settingTemplate);

      if (validateSettingTemplate) {
        this.context.dispatch("Loading/IsLoading", true, { root: true });
        this.context.commit("MutationSettingsUpdateLoadingStatus", LoadingStatusType.Loading);
        const settingModel: SettingModel = ConvertToSettingModel(input.settingTemplate);

        API.SettingService.DoUpdateSetting(settingModel).then((result) => {
          if (result.data.responseCode.IsSuccess()) {
            const lang = i18n.locale;
            const settingWord = i18n.t("Setting");
            const title = input.settingTitle;

            const message = lang === "en" ? `${settingWord} ${title}` : `${settingWord}${title}`;

            alertMessage = i18n.t("Update_Message_Success", {
              message,
            });
            typeMessage = TypeComponentNoti.Success;
            this.context.commit("MutationSettingsUpdateLoadingStatus", LoadingStatusType.Success);
            this.context.dispatch("Loading/IsLoading", false, { root: true });
          } else {
            alertMessage = GetErrorMessageByPayrollExceptionCode(result.data.exception);
            typeMessage = TypeComponentNoti.Error;
          }
          this.context.dispatch(
            "Alert/SetAlertMessage",
            {
              alertMessage: alertMessage,
              typeMessage: typeMessage,
            },
            { root: true }
          );
        });
      } else {
        alertMessage = i18n.t("Please_Fill_In_Valid_Value");
        typeMessage = TypeComponentNoti.Error;
        this.context.dispatch(
          "Alert/SetAlertMessage",
          {
            alertMessage: alertMessage,
            typeMessage: typeMessage,
          },
          { root: true }
        );
      }
    }
  }
  @Action DoGetMerchantDetail() {
    if (this.merchantDetailLoadingStatus !== LoadingStatusType.Loading) {
      this.context.dispatch("Loading/IsLoading", true, { root: true });
      this.context.commit("MutationMerchantDetailLoadingStatus", LoadingStatusType.Loading);
      API.MerchantService.GetMerchantDetail().then((resMerchantDetail: any) => {
        if (resMerchantDetail.status.IsSuccess()) {
          const detail = ConvertToMerchantDetail(resMerchantDetail.data.detail);
          this.context.commit("MutationMerchantDetail", detail);
        }
        this.context.commit("MutationMerchantDetailLoadingStatus", LoadingStatusType.Success);
        this.context.dispatch("Loading/IsLoading", false, { root: true });
      });
    }
  }
  @Action DoGetMerchantAddressList() {
    if (this.merchantDetailLoadingStatus !== LoadingStatusType.Loading) {
      this.context.dispatch("Loading/IsLoading", true, { root: true });
      this.context.commit("MutationMerchantAddressListLoadingStatus", LoadingStatusType.Loading);
      API.MerchantService.GetMerchantAddressList().then((resMerchantAddressList: any) => {
        if (resMerchantAddressList.status.IsSuccess()) {
          const addressList = ConvertToMerchantAddressList(resMerchantAddressList.data.addressList);
          this.context.commit("MutationMerchantAddressList", addressList);
        }
        this.context.commit("MutationMerchantAddressListLoadingStatus", LoadingStatusType.Success);
        this.context.dispatch("Loading/IsLoading", false, { root: true });
      });
    }
  }

  @Action DoGetMerchantDetailPublicLink(merchantId: number) {
    if (this.merchantDetailLoadingStatus !== LoadingStatusType.Loading) {
      this.context.dispatch("Loading/IsLoading", true, { root: true });
      this.context.commit("MutationMerchantDetailLoadingStatus", LoadingStatusType.Loading);
      API.MerchantService.GetMerchantDetailPublicLink(merchantId).then((resMerchantDetail: any) => {
        if (resMerchantDetail.status.IsSuccess()) {
          const detail = ConvertToMerchantDetail(resMerchantDetail.data.detail);
          this.context.commit("MutationMerchantDetail", detail);
        }
        this.context.commit("MutationMerchantDetailLoadingStatus", LoadingStatusType.Success);
        this.context.dispatch("Loading/IsLoading", false, { root: true });
      });
    }
  }

  @Action DoGetMerchantDetailByPasscode(merchantId: number) {
    if (this.merchantDetailLoadingStatus !== LoadingStatusType.Loading) {
      this.context.dispatch("Loading/IsLoading", true, { root: true });
      this.context.commit("MutationMerchantDetailLoadingStatus", LoadingStatusType.Loading);
      API.MerchantService.DoGetMerchantDetailByPasscode(merchantId).then((resMerchantDetail: any) => {
        if (resMerchantDetail.status.IsSuccess()) {
          const detail = ConvertToMerchantDetail(resMerchantDetail.data.detail);
          this.context.commit("MutationMerchantDetail", detail);
        }
        this.context.commit("MutationMerchantDetailLoadingStatus", LoadingStatusType.Success);
        this.context.dispatch("Loading/IsLoading", false, { root: true });
      });
    }
  }

  @Action
  public DoUpdateMerchantPayrollContact(input: any) {
    if (this.updatePayrollContactLoadingStatus !== LoadingStatusType.Loading) {
      this.context.dispatch("Loading/IsLoading", true, { root: true });
      let alertMessage: any = "";
      let typeMessage: TypeComponentNoti = TypeComponentNoti.success;
      this.context.commit("MutationUpdatePayrollContactLoadingStatus", LoadingStatusType.Loading);
      API.MerchantService.DoUpdateMerchantPayrollContact(input).then((resUpdateMerchantPayrollContact) => {
        if (resUpdateMerchantPayrollContact.data.responseCode.IsSuccess()) {
          alertMessage = i18n.t("Update_Message_Success", {
            message: input.settingTitle,
          });
          typeMessage = TypeComponentNoti.Success;
          this.context.commit("MutationUpdatePayrollContactLoadingStatus", LoadingStatusType.Success);
          this.context.commit("MutationMerchantDetailLoadingStatus", LoadingStatusType.None);
        } else {
          alertMessage = GetErrorMessageByPayrollExceptionCode(resUpdateMerchantPayrollContact.data.exception);
          typeMessage = TypeComponentNoti.Error;
          this.context.commit("MutationUpdatePayrollContactLoadingStatus", LoadingStatusType.None);
        }
        this.context.dispatch(
          "Alert/SetAlertMessage",
          {
            alertMessage: alertMessage,
            typeMessage: typeMessage,
          },
          { root: true }
        );
        this.context.dispatch("Loading/IsLoading", false, { root: true });
      });
    }
  }
  @Action
  public DoUpdateOnBoarding(input: any) {
    if (this.updateOnBoardingStatus !== LoadingStatusType.Loading) {
      this.context.commit("MutationUpdateOnBoardingStatus", LoadingStatusType.Loading);
      API.SettingService.DoUpdateOnBoarding(input).then((result: any) => {
        this.context.commit("MutationUpdateOnBoardingStatus", LoadingStatusType.Success);
        this.context.commit("MutationOnBoardingLoadingStatus", LoadingStatusType.None);
      });
    }
  }
  @Action
  public DoGetOnBoarding() {
    if (this.onBoardingLoadingStatus !== LoadingStatusType.Loading) {
      this.context.commit("MutationOnBoardingLoadingStatus", LoadingStatusType.Loading);
      API.SettingService.DoGetOnBoarding().then((resOnBoarding) => {
        if (resOnBoarding.status.IsSuccess()) {
          const detail = ConvertToOnBoarding(resOnBoarding.data.detail);
          this.context.commit("MutationOnBoarding", detail);
        }
        this.context.commit("MutationOnBoardingLoadingStatus", LoadingStatusType.Success);
      });
    }
  }

  @Action
  public async DoGetDocumentSettings(input: Parameters<(typeof API)["SettingService"]["DoGetDocumentSettings"]>[0]) {
    if (this.documentSettingsLoadingStatus !== LoadingStatusType.Loading) {
      this.context.commit("MutationDocumentSettingsLoadingStatus", LoadingStatusType.Loading);

      try {
        const res = await API.SettingService.DoGetDocumentSettings(input);

        if (res.status.IsSuccess()) {
          this.context.commit("MutationDocumentSettings", res.data.detail);
        }
        this.context.commit("MutationDocumentSettingsLoadingStatus", LoadingStatusType.Success);
      } catch (err) {
        this.context.commit("MutationDocumentSettingsLoadingStatus", LoadingStatusType.Fail);
      }
    }
  }

  @Action
  public async DoUpdateDocumentSettings(
    input: Parameters<(typeof API)["SettingService"]["DoUpdateDocumentSettings"]>[0]
  ) {
    if (this.updateDocumentSettingsLoadingStatus !== LoadingStatusType.Loading) {
      this.context.commit("MutationUpdateDocumentSettingsLoadingStatus", LoadingStatusType.Loading);

      let alertMessage: any = "";
      const message = {
        [PayrollDocumentSettings.PND1kor]: i18n.t("PND_1_Kor_Save_Success"),
        [PayrollDocumentSettings.PND1korItem]: i18n.t("PND_1_Kor_Item_Save_Success"),
        [PayrollDocumentSettings.WithholdingTax]: i18n.t("Withholding_Tax_Save_Success"),
      };
      let typeMessage: TypeComponentNoti = TypeComponentNoti.success;

      try {
        const res = await API.SettingService.DoUpdateDocumentSettings(input);

        if (res.status.IsSuccess()) {
          this.context.commit("MutationUpdateDocumentSettings", res.data.detail);
          this.context.commit("MutationUpdateDocumentSettingsLoadingStatus", LoadingStatusType.Success);

          alertMessage = message[input.data.documentType];
          typeMessage = TypeComponentNoti.Success;

          this.context.dispatch(
            "Alert/SetAlertMessage",
            {
              alertMessage: alertMessage,
              typeMessage: typeMessage,
            },
            { root: true }
          );
        }
      } catch (err) {
        this.context.commit("MutationUpdateDocumentSettingsLoadingStatus", LoadingStatusType.Fail);

        alertMessage = i18n.t("Update_Error", {
          message: i18n.t("Update_Error"),
        });
        typeMessage = TypeComponentNoti.Error;

        this.context.dispatch(
          "Alert/SetAlertMessage",
          {
            alertMessage: alertMessage,
            typeMessage: typeMessage,
          },
          { root: true }
        );
      }
    }
  }

  @Action
  public DoUpdateCurrentDocumentSettings(input: CurrentDocumentSettings) {
    this.context.commit("MutationCurrentDocumentSettings", input);
  }
}
